import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import { Button } from "@jambushrushti/components/ui/button";
import { Link } from "react-router-dom";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Database,
  Search,
  FileText,
  Globe,
  ArrowRight,
  Sparkles,
  CheckCircle,
  Users,
} from "lucide-react";

const tools = [
  {
    id: "sodhsanchay",
    title: "SodhSanchay",
    subtitle: "Research Metadata Entry",
    description:
      "A centralized database to store structured research notes, metadata, and scholarly references. Perfect for organizing your research journey.",
    icon: Database,
    href: "/research/sodhsanchay",
    borderColor: "border-l-blue-500",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    subtitleColor: "text-blue-600",
    buttonBg: "bg-primary hover:bg-primary/90",
    features: [
      "Structured metadata entry",
      "Tag-based organization",
      "Link to books & manuscripts",
      "AI-powered tag suggestions",
    ],
  },
  {
    id: "sodhsandarbh",
    title: "SodhSandarbh",
    subtitle: "Search & Export Research",
    description:
      "Powerful search across all research entries with advanced filters, Boolean logic, and easy export to Excel/CSV for offline analysis.",
    icon: Search,
    href: "/research/sodhsandarbh",
    borderColor: "border-l-orange-500",
    iconBg: "bg-orange-100",
    iconColor: "text-orange-600",
    subtitleColor: "text-orange-600",
    buttonBg: "bg-primary hover:bg-primary/90",
    features: [
      "Advanced search filters",
      "Boolean search (AND, OR, NOT)",
      "Export to Excel/CSV",
      "Linked entry viewing",
    ],
  },
  {
    id: "shabdasangraha",
    title: "Śabdasaṅgraha",
    subtitle: "Smart PDF Search Engine",
    description:
      "Search inside Jain texts like Google. Find exact pages matching your query with AI-powered transliteration support for multi-language searches.",
    icon: FileText,
    href: "/research/shabdasangraha",
    borderColor: "border-l-emerald-500",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    subtitleColor: "text-emerald-600",
    buttonBg: "bg-primary hover:bg-primary/90",
    features: [
      "Full-text PDF search",
      "Page-level results",
      "Transliteration support",
      "Multi-language queries",
    ],
  },
  {
    id: "shastrasandarbha",
    title: "Shastrasandarbha",
    subtitle: "AI Global Book Finder",
    description:
      "AI-powered search across Archive.org, HathiTrust, JSTOR, Jain eLibrary, and more. Find Jain books from digital libraries worldwide.",
    icon: Globe,
    href: "/research/shastrasandarbha",
    borderColor: "border-l-purple-500",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
    subtitleColor: "text-purple-600",
    buttonBg: "bg-primary hover:bg-primary/90",
    features: [
      "Multi-source aggregation",
      "AI result ranking",
      "Duplicate removal",
      "Direct external links",
    ],
  },
];

const Research = () => {
  return (
    <Layout>
      <Seo
        title="Research Tools"
        description="Powerful AI-powered research tools for exploring Jain scriptures and teachings of Gurudev Muni Jambuvijayaji Maharaj Saheb - SodhSanchay, SodhSandarbh, Shabdasangraha, and more."
      />
      {/* Tools Section */}
      <section className="bg-background py-5 sm:py-8 lg:py-16">
        <div className="container-wide mx-auto px-4">
          {/* Section Header */}
          <div className="mb-6 text-center sm:mb-10">
            <Badge
              variant="outline"
              className="mb-4 border-primary/30 text-primary"
            >
              <Sparkles className="mr-2 h-3.5 w-3.5" />
              Research Tools
            </Badge>
            <h1 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
              Powerful Tools for Deep Research
            </h1>
            <p className="font-body mx-auto max-w-2xl text-lg text-muted-foreground">
              Each tool is designed to work seamlessly together, creating a
              comprehensive research ecosystem.
            </p>
          </div>

          {/* Tools Grid - 2x2 with left border accent */}
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
            {tools.map((tool, index) => (
              <div
                key={tool.id}
                className={`group rounded-2xl border-l-4 bg-card ${tool.borderColor} overflow-hidden shadow-md transition-all duration-300 hover:shadow-lg`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="p-6 lg:p-8">
                  {/* Header with Icon */}
                  <div className="mb-4 flex items-start gap-4">
                    <div
                      className={`h-12 w-12 shrink-0 rounded-xl ${tool.iconBg} flex items-center justify-center`}
                    >
                      <tool.icon className={`h-6 w-6 ${tool.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-bold text-foreground">
                        {tool.title}
                      </h3>
                      <p
                        className={`font-body text-base font-medium ${tool.subtitleColor}`}
                      >
                        {tool.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="font-body mb-5 text-base leading-relaxed text-muted-foreground">
                    {tool.description}
                  </p>

                  {/* Features - 2 columns */}
                  <div className="mb-6 grid grid-cols-2 gap-x-4 gap-y-2">
                    {tool.features.map(feature => (
                      <div
                        key={feature}
                        className="font-body flex items-center gap-2 text-base text-muted-foreground"
                      >
                        <CheckCircle
                          className={`h-4 w-4 shrink-0 ${tool.iconColor}`}
                        />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <Button
                    asChild
                    className={`w-full ${tool.buttonBg} rounded-lg text-primary-foreground`}
                  >
                    <Link to={tool.href}>
                      Open {tool.title}
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-secondary/30 py-6 sm:py-10 lg:py-14">
        <div className="container-wide mx-auto px-4">
          <div className="mb-6 text-center sm:mb-10">
            <h2 className="font-heading mb-3 text-2xl font-bold text-foreground lg:text-3xl">
              How It Works
            </h2>
            <p className="font-body text-base text-muted-foreground">
              A seamless research workflow from start to finish
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-10">
            {[
              {
                icon: Database,
                label: "Store",
                desc: "Organize your research",
                bg: "bg-blue-500",
              },
              {
                icon: Search,
                label: "Search",
                desc: "Find & export data",
                bg: "bg-orange-500",
              },
              {
                icon: FileText,
                label: "Explore",
                desc: "Search inside PDFs",
                bg: "bg-emerald-500",
              },
              {
                icon: Globe,
                label: "Discover",
                desc: "Global book search",
                bg: "bg-purple-500",
              },
            ].map((item, index) => (
              <div key={item.label} className="flex items-center gap-6">
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`h-14 w-14 rounded-full ${item.bg} mb-3 flex items-center justify-center shadow-md`}
                  >
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="font-heading font-semibold text-foreground">
                    {item.label}
                  </p>
                  <p className="font-body text-base text-muted-foreground">
                    {item.desc}
                  </p>
                </div>
                {index < 3 && (
                  <ArrowRight className="hidden h-5 w-5 text-muted-foreground/40 lg:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-primary py-6 sm:py-10 lg:py-14">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute right-20 top-10 h-64 w-64 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-10 left-10 h-48 w-48 rounded-full bg-gold blur-3xl" />
        </div>

        <div className="container-wide relative z-10 mx-auto px-4 text-center">
          <Badge
            variant="outline"
            className="mb-5 border-white/20 bg-white/10 text-white"
          >
            <Users className="mr-2 h-3.5 w-3.5" />
            Join Our Community
          </Badge>

          <h2 className="font-heading mb-4 text-3xl font-bold text-white lg:text-4xl">
            Ready to Start Your Research?
          </h2>
          <p className="font-body mx-auto mb-8 max-w-lg text-base text-white/80">
            Join our scholarly community and get access to all research tools.
            Registered scholars can also contribute to the research database.
          </p>

          <div className="mx-auto grid max-w-md grid-cols-2 gap-2.5 sm:flex sm:max-w-none sm:flex-wrap sm:justify-center sm:gap-4">
            <Button
              size="lg"
              asChild
              className="w-full rounded-lg bg-white px-2.5 text-xs font-semibold text-primary hover:bg-white/90 sm:w-auto sm:px-6 sm:text-sm"
            >
              <Link to="/auth" className="justify-center">
                Create Account
                <ArrowRight className="ml-1 h-3.5 w-3.5 shrink-0 sm:ml-2 sm:h-4 sm:w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Research;
